import { Form } from 'antd'
import { t } from 'i18next'
import { useCallback, useMemo, useState } from 'react'
import { ModalBase, ModalContentContainer } from '../../../../../components'
import { optionalRequiredMark } from '../../../../../constants/constants'
import SmartFunctionActionDropdownDropDownItem from '../../../components/SmartFunctionActionDropdown/SmartFunctionActionDropdownDropDownItem'
import CompensationMatrixAnalysisConfigurationModalFooter from './footer/CompensationMatrixAnalysisConfigurationModalFooter'
import CompensationMatrixAnalysisConfigurationModalStep from './Steps/CompensationMatrixAnalysisConfigurationModalStep'
import {
  notificationService,
  smartFunctionService,
} from '../../../../../services'
import { useNavigate, useParams } from 'react-router-dom'
import { useWorkspaceDetails } from '../../../../../hooks'
import { AxiosError } from 'axios'
import {
  AlgorithmRun,
  AlgorithmRunCompensationMatrixAnalysisConfig,
} from '../../../../../store/reducers/workspaceReducer'

const CompensationMatrixAnalysisConfigurationModal = () => {
  const [form] = Form.useForm<AlgorithmRunCompensationMatrixAnalysisConfig>()
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  const { selectedWorkspace, refreshWorkspaceDetails } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })
  const [open, setOpen] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)

  const resetValues = useCallback(() => {
    form.resetFields()
  }, [form])

  const handleOk = useCallback(() => {
    setConfirmLoading(true)
    form.submit()
  }, [form])

  const handleCancel = useCallback(() => {
    resetValues()
    setOpen(false)
  }, [resetValues])

  const handleSubmit = useCallback(async () => {
    try {
      const algorithmRunCompensationMatrixAnalysisConfig: AlgorithmRunCompensationMatrixAnalysisConfig =
        {
          ssr: form.getFieldValue('ssr'),
          experimentName: form.getFieldValue('experimentName'),
          manuallySelectExperimentRun: form.getFieldValue(
            'manuallySelectExperimentRun',
          ),
          selectedExperimentRuns: form
            .getFieldValue('selectedExperimentRuns')
            .map((run: { label: string }) => run.label),
        }
      resetValues()

      smartFunctionService
        .runAlgorithm({
          id: selectedWorkspace?.id || '',
          navigate,
          config: algorithmRunCompensationMatrixAnalysisConfig,
        })
        .then((response: AlgorithmRun) => {
          refreshWorkspaceDetails()
          notificationService.notificationSuccess({
            messageToDisplay: t('smartFunctionDetailsPage.runStarted'),
          })
          setOpen(false)
        })
        .catch((error: AxiosError | Error) => {
          console.error('axios fetch error', error)
        })
    } catch (error) {
      console.error('Validation Failed:', error)
    } finally {
      setConfirmLoading(false)
    }
  }, [form, resetValues, navigate, refreshWorkspaceDetails, selectedWorkspace])

  const defaultValues: AlgorithmRunCompensationMatrixAnalysisConfig = useMemo(
    () => ({
      ssr: undefined,
      experimentName: undefined,
      manuallySelectExperimentRun: false,
      selectedExperimentRuns: [],
    }),
    [],
  )

  return (
    <>
      <SmartFunctionActionDropdownDropDownItem
        onClick={() => {
          setOpen(true)
        }}
      >
        {t(
          'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.configureRun',
        )}
      </SmartFunctionActionDropdownDropDownItem>
      <ModalBase
        key="main"
        title={t('compensationMatrixAnalysisConfigurationModal.title')}
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        destroyOnClose
        width={720}
        footer={
          <CompensationMatrixAnalysisConfigurationModalFooter
            onCancel={handleCancel}
            onSubmit={handleSubmit}
          />
        }
        styles={{
          body: {
            maxHeight: 'calc(100vh - 400px)',
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={defaultValues}
          requiredMark={optionalRequiredMark}
          onFinish={handleSubmit}
        >
          <ModalContentContainer>
            <CompensationMatrixAnalysisConfigurationModalStep form={form} />
          </ModalContentContainer>
        </Form>
      </ModalBase>
    </>
  )
}

export default CompensationMatrixAnalysisConfigurationModal
